import React from 'react';
import { 
  WifiIcon, 
  NoSymbolIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

interface OfflineIndicatorProps {
  isOnline: boolean;
  syncStatus?: 'idle' | 'syncing' | 'error';
  pendingActions?: number;
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  isOnline, 
  syncStatus = 'idle', 
  pendingActions = 0 
}) => {
  if (isOnline && syncStatus === 'idle' && pendingActions === 0) {
    return null;
  }

  const getStatusContent = () => {
    if (!isOnline) {
      return {
        icon: <NoSymbolIcon className="w-4 h-4" />,
        text: 'Offline',
        subText: pendingActions > 0 ? `${pendingActions} actions pending` : 'Changes will sync when connected',
        bgColor: 'bg-red-500',
        textColor: 'text-white'
      };
    }

    if (syncStatus === 'syncing') {
      return {
        icon: <ArrowPathIcon className="w-4 h-4 animate-spin" />,
        text: 'Syncing',
        subText: `Syncing ${pendingActions} pending actions...`,
        bgColor: 'bg-blue-500',
        textColor: 'text-white'
      };
    }

    if (syncStatus === 'error') {
      return {
        icon: <ExclamationTriangleIcon className="w-4 h-4" />,
        text: 'Sync Error',
        subText: 'Some changes failed to sync. Will retry automatically.',
        bgColor: 'bg-orange-500',
        textColor: 'text-white'
      };
    }

    if (pendingActions > 0) {
      return {
        icon: <WifiIcon className="w-4 h-4" />,
        text: 'Connected',
        subText: `${pendingActions} actions will sync shortly`,
        bgColor: 'bg-green-500',
        textColor: 'text-white'
      };
    }

    return null;
  };

  const content = getStatusContent();
  if (!content) return null;

  return (
    <>
      {/* Mobile indicator */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40">
        <div className={`${content.bgColor} ${content.textColor} px-4 py-2 text-sm font-medium flex items-center justify-center gap-2`}>
          {content.icon}
          <span>{content.text}</span>
        </div>
      </div>

      {/* Desktop indicator */}
      <div className="hidden md:block fixed top-4 right-4 z-40">
        <div className={`${content.bgColor} ${content.textColor} px-4 py-3 rounded-lg shadow-lg max-w-xs`}>
          <div className="flex items-center gap-2 mb-1">
            {content.icon}
            <span className="font-medium">{content.text}</span>
          </div>
          <p className="text-xs opacity-90">{content.subText}</p>
        </div>
      </div>
    </>
  );
};

export default OfflineIndicator;